import React, { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import db from '../lib/database';
import { useStore, useNav } from '../store/StoreContext';
import {
    getClientStatuses,
    getEstimateStatuses,
    getOperationsStatuses,
} from '../lib/constants';
import { fieldLabel, formatValue } from '../components/ActivityLog';
import { ChoiceKeys, Combobox, Segmented, Button, PageHeader, Toolbar, FilterChips, EmptyState, RowSkeleton, pageWrapCls, tableWrapCls, tableCls, thCls, trCls, trClickCls, tdCls } from '../components/ui';
import { IconActivity } from '../components/icons';

// Global Activity page — admin/sales-tracking view of every change made
// across clients, estimates, and dispatch jobs.
//
// Use case (per Jaylen): see at a glance what the office team has been
// doing. Spot lead drops, track who's converting leads to bookings, find
// patterns in changes. The per-record ActivityLog component covers "what
// happened to this client?" — this page covers "what did Mike do this
// week?" and "show me all lead drops this month."
//
// Architecture:
// • Reuses fieldLabel + formatValue from ActivityLog so diff rendering
//   is consistent between the per-record sidebar and this page.
// • Breadcrumb (Client: Smith, Estimate: 5/12 LOCAL, Job: 5/13 8am) is
//   resolved from the store's in-memory `clients` / `estimates` / `jobs`
//   arrays — no additional fetches per row.
// • Filters trigger a refetch via the useEffect dep array.
// • Quick-filter chips set canonical filter combos with one click.

// Same shape as the per-record context — we resolve status keys across
// all three status sets so any audit row's status diff gets a friendly label.
function useStatusCtx() {
    const { config } = useStore();
    return useMemo(() => ({
        allStatuses: [
            ...getClientStatuses(config),
            ...getEstimateStatuses(config),
            ...getOperationsStatuses(config),
        ],
    }), [config]);
}

// Convert a table_name + record_id into a human-readable breadcrumb. Uses
// in-memory store data for resolution — no additional fetches required.
function breadcrumbFor(row, store) {
    if (row.table_name === 'clients') {
        const c = (store.clients || []).find(c => c.id === row.record_id);
        return {
            label: c?.name || 'Client',
            sublabel: c?.phone || '',
            kind: 'Client',
        };
    }
    if (row.table_name === 'estimates') {
        const e = (store.estimates || []).find(e => e.id === row.record_id);
        if (!e) return { label: 'Estimate', sublabel: '', kind: 'Estimate' };
        const c = (store.clients || []).find(c => c.id === e.client_id);
        const dateLabel = e.move_date ? format(parseISO(e.move_date + 'T12:00:00'), 'MMM d') : '';
        return {
            label: c?.name || 'Unknown client',
            sublabel: [dateLabel, e.job_type].filter(Boolean).join(' • '),
            kind: 'Estimate',
        };
    }
    if (row.table_name === 'move_jobs') {
        const j = (store.jobs || []).find(j => j.id === row.record_id);
        if (!j) return { label: 'Job', sublabel: '', kind: 'Job' };
        const c = (store.clients || []).find(c => c.id === j.client_id);
        const dateLabel = j.move_date ? format(parseISO(j.move_date + 'T12:00:00'), 'MMM d') : '';
        return {
            label: c?.name || 'Unknown client',
            sublabel: [dateLabel, j.time, j.type].filter(Boolean).join(' • '),
            kind: 'Job',
        };
    }
    return { label: row.record_id, sublabel: '', kind: row.table_name };
}

// ─── Filter presets ────────────────────────────────────────────────────

const TIME_RANGES = [
    { key: 'today',  label: 'Today',     ms: 24 * 60 * 60 * 1000 },
    { key: '7d',     label: '7 days',    ms: 7  * 24 * 60 * 60 * 1000 },
    { key: '30d',    label: '30 days',   ms: 30 * 24 * 60 * 60 * 1000 },
    { key: 'all',    label: 'All time',  ms: null },
];

// Quick-filter buttons — preset combos that snap the filter bar to a
// canonical query. "Lead drops" is the headline accountability use case.
const QUICK_FILTERS = [
    {
        key: 'all',
        label: 'All activity',
        description: null,
        apply: () => ({ tableName: '', operation: '', statusTo: '' }),
    },
    {
        key: 'lead-drops',
        label: 'Lead drops',
        description: 'Clients moved to inactive',
        apply: () => ({ tableName: 'clients', operation: 'update', statusTo: 'inactive' }),
    },
    {
        key: 'new-bookings',
        label: 'New bookings',
        description: 'Estimates marked booked',
        apply: () => ({ tableName: 'estimates', operation: 'update', statusTo: 'booked' }),
    },
    {
        key: 'new-clients',
        label: 'New clients',
        description: 'Clients just created',
        apply: () => ({ tableName: 'clients', operation: 'insert', statusTo: '' }),
    },
];

// ─── Row component ─────────────────────────────────────────────────────

function AuditRow({ row, ctx, store, onOpen }) {
    const when = useMemo(() => {
        try { return formatDistanceToNow(parseISO(row.occurred_at), { addSuffix: true }); }
        catch { return row.occurred_at; }
    }, [row.occurred_at]);
    const whenAbs = useMemo(() => {
        try { return format(parseISO(row.occurred_at), 'MMM d, yyyy h:mm a'); }
        catch { return row.occurred_at; }
    }, [row.occurred_at]);

    const breadcrumb = useMemo(() => breadcrumbFor(row, store), [row, store]);
    const actor = row.user_name || (row.user_email ? row.user_email.split('@')[0] : null) || 'System';
    const canOpen = ['clients', 'estimates', 'move_jobs'].includes(row.table_name);

    let body;
    if (row.operation === 'insert') {
        body = <span className="text-[12px] text-slate-500 italic">Created</span>;
    } else if (row.operation === 'delete') {
        body = <span className="text-[12px] text-rose-600 italic">Deleted</span>;
    } else {
        const fields = row.changes ? Object.keys(row.changes) : [];
        body = (
            <ul className="text-[12px] text-slate-600 space-y-0.5">
                {fields.map((f) => {
                    const diff = row.changes[f] || {};
                    const fromVal = formatValue(f, diff.from, ctx);
                    const toVal = formatValue(f, diff.to, ctx);
                    return (
                        <li key={f} className="leading-snug">
                            <span className="font-semibold text-slate-700">{fieldLabel(f)}:</span>{' '}
                            <span className="text-slate-500">{fromVal}</span>
                            <span className="text-slate-400 mx-1">→</span>
                            <span className="text-slate-800 font-medium">{toVal}</span>
                        </li>
                    );
                })}
            </ul>
        );
    }

    return (
        <tr
            className={canOpen ? trClickCls : trCls}
            onClick={() => canOpen && onOpen?.(row)}
        >
            {/* Multi-line cells, so top-align (overrides tdCls's align-middle). */}
            <td className={`${tdCls} !align-top whitespace-nowrap w-32`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{breadcrumb.kind}</div>
                <div className="text-[13px] font-semibold text-slate-800 truncate">{breadcrumb.label}</div>
                {breadcrumb.sublabel && <div className="text-[11px] text-slate-500 truncate">{breadcrumb.sublabel}</div>}
            </td>
            <td className={`${tdCls} !align-top`}>{body}</td>
            <td className={`${tdCls} !align-top whitespace-nowrap w-40`}>
                <div className="text-[12px] font-semibold text-slate-700">{actor}</div>
                <div className="text-[10px] text-slate-400 tabular-nums" title={whenAbs}>{when}</div>
            </td>
        </tr>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────

export default function AuditPage() {
    const store = useStore();
    const nav = useNav();
    const ctx = useStatusCtx();

    const [rows, setRows] = useState(null); // null=loading, []=empty
    const [error, setError] = useState(null);
    const [actors, setActors] = useState([]);

    // Filters
    const [userId, setUserId] = useState('');
    const [tableName, setTableName] = useState('');
    const [operation, setOperation] = useState('');
    const [statusTo, setStatusTo] = useState('');
    const [timeRange, setTimeRange] = useState('7d');
    const [quickFilter, setQuickFilter] = useState('all');

    // Compute sinceIso from the time range preset.
    const sinceIso = useMemo(() => {
        const t = TIME_RANGES.find(t => t.key === timeRange);
        if (!t || t.ms == null) return null;
        return new Date(Date.now() - t.ms).toISOString();
    }, [timeRange]);

    // Fetch on mount + whenever filters change.
    useEffect(() => {
        let cancelled = false;
        setRows(null);
        setError(null);
        db.getAuditLogList({
            userId: userId || undefined,
            tableName: tableName || undefined,
            operation: operation || undefined,
            sinceIso: sinceIso || undefined,
            statusTo: statusTo || undefined,
            limit: 300,
        })
            .then((data) => { if (!cancelled) setRows(data); })
            .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load activity'); });
        return () => { cancelled = true; };
    }, [userId, tableName, operation, sinceIso, statusTo]);

    // One-time fetch for the user dropdown options.
    useEffect(() => {
        db.getAuditLogActors().then(setActors).catch(() => {});
    }, []);

    const applyQuickFilter = (qf) => {
        setQuickFilter(qf.key);
        const f = qf.apply();
        setTableName(f.tableName);
        setOperation(f.operation);
        setStatusTo(f.statusTo);
    };

    const handleOpen = (row) => {
        if (row.table_name === 'clients') nav.navigateToClient?.(row.record_id);
        else if (row.table_name === 'estimates') nav.navigateToEstimate?.(row.record_id);
        else if (row.table_name === 'move_jobs') {
            // Find the job's move_date so dispatch focuses on the right week.
            const j = (store.jobs || []).find(j => j.id === row.record_id);
            nav.navigateToDispatch?.(j?.move_date || null);
        }
    };

    const timeLabel = (TIME_RANGES.find(t => t.key === timeRange) || {}).label || '';
    const actorCount = Array.isArray(rows) ? new Set(rows.map(r => r.user_id || r.user_email || 'system')).size : 0;
    const summary = Array.isArray(rows)
        ? `${rows.length >= 300 ? '300+' : rows.length} ${rows.length === 1 ? 'event' : 'events'} · ${actorCount} ${actorCount === 1 ? 'user' : 'users'} · ${timeLabel}`
        : timeLabel;

    const clearFilters = () => {
        setUserId('');
        setTableName('');
        setOperation('');
        setStatusTo('');
        setQuickFilter('all');
        setTimeRange('all');
    };

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Activity"
                summary={summary}
                help={<>
                    <p>Every change made to clients, estimates, and dispatch jobs — who did it, what changed, and when. Click a row to open the record it belongs to.</p>
                    <p className="mt-2">The quick filters snap the bar to a canonical query: <strong>Lead drops</strong> is clients moved to inactive, <strong>New bookings</strong> is estimates marked booked. Editing a filter by hand clears the quick filter.</p>
                </>}
            />

            {/* Toolbar — user, quick filters (preset combos that snap the
                filter bar to canonical sales-tracking queries), record type,
                action, time range. Quick filters drive the ChoiceKeys (they
                call setX); manual edits there clear the quick-filter
                highlight. */}
            <Toolbar>
                <Combobox
                    size="sm"
                    ariaLabel="User"
                    placeholder="All users"
                    items={actors.map(a => ({ value: a.user_id, label: a.user_name || a.user_email }))}
                    value={userId}
                    onChange={v => setUserId(v)}
                    className="w-52"
                />
                <FilterChips
                    ariaLabel="Quick filter"
                    items={QUICK_FILTERS.map(qf => ({ key: qf.key, label: qf.label }))}
                    value={quickFilter}
                    onChange={k => { const qf = QUICK_FILTERS.find(q => q.key === k); if (qf) applyQuickFilter(qf); }}
                />
                <ChoiceKeys
                    size="sm"
                    ariaLabel="Record type"
                    allowEmpty emptyLabel="All records"
                    items={[
                        { value: 'clients', label: 'Clients' },
                        { value: 'estimates', label: 'Estimates' },
                        { value: 'move_jobs', label: 'Jobs' },
                    ]}
                    value={tableName}
                    onChange={v => { setTableName(v); setQuickFilter('all'); }}
                />
                <ChoiceKeys
                    size="sm"
                    ariaLabel="Action"
                    allowEmpty emptyLabel="Any action"
                    items={[
                        { value: 'insert', label: 'Created' },
                        { value: 'update', label: 'Updated' },
                        { value: 'delete', label: 'Deleted' },
                    ]}
                    value={operation}
                    onChange={v => { setOperation(v); setQuickFilter('all'); }}
                />
                <Segmented
                    size="sm"
                    ariaLabel="Time range"
                    items={TIME_RANGES.map(t => ({ value: t.key, label: t.label }))}
                    value={timeRange}
                    onChange={v => setTimeRange(v)}
                    className="ml-auto"
                />
            </Toolbar>

            {/* List — ledger recipe, see BBM Context for claude/lists.md §3 */}
            <div className={tableWrapCls}>
                {rows === null && <RowSkeleton rows={5} />}
                {error && (
                    <div className="px-4 py-3 text-sm font-semibold text-red-600 bg-red-50 border-b border-red-200">{error}</div>
                )}
                {Array.isArray(rows) && rows.length === 0 && (
                    <EmptyState
                        icon={<IconActivity size={28} />}
                        title="No events match"
                        hint="Try widening the time range or clearing the filters."
                        action={<Button variant="tertiary" size="sm" onClick={clearFilters}>Clear filters</Button>}
                        className="border-0 rounded-none"
                    />
                )}
                {Array.isArray(rows) && rows.length > 0 && (
                    <table className={tableCls}>
                        <thead>
                            <tr>
                                <th className={`${thCls} w-32`}>Record</th>
                                <th className={thCls}>Change</th>
                                <th className={`${thCls} w-40`}>By / When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <AuditRow
                                    key={row.id}
                                    row={row}
                                    ctx={ctx}
                                    store={store}
                                    onOpen={handleOpen}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {Array.isArray(rows) && rows.length >= 300 && (
                <p className="text-[11px] text-slate-400 text-center">
                    Showing the 300 most recent matching events. Tighten the filters or narrow the time range to see older activity.
                </p>
            )}
        </div>
    );
}
